/**
 * @fileoverview Bank Details page — the bank's static profile fields.
 * @description Ported from the InsightBox Banking design system's own `BankDetails.tsx`. Unlike
 * every other Banking page, this one doesn't touch `useBankingData()`/`derived.ts` at all — its
 * content is the static `BANK_DETAILS` object, so there's no loading/error state to render.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import { BANK_DETAILS } from '@/features/banking'
import PageHeader from '@/shared/components/molecules/PageHeader'

interface DetailSectionProps {
  title: string
  rows: { label: string; value: string }[]
}

function DetailSection({ title, rows }: DetailSectionProps) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-card">
      <div className="bg-accent px-6 py-3.5">
        <h2 className="text-sm font-bold tracking-wide text-white uppercase">{title}</h2>
      </div>
      <dl className="divide-y divide-border">
        {rows.map((row) => (
          <div
            key={row.label}
            className="grid grid-cols-[repeat(auto-fit,minmax(min(160px,100%),1fr))] items-center gap-1 px-6 py-3.5 sm:gap-6"
          >
            <dt className="text-sm text-label">{row.label}</dt>
            <dd className="text-left text-sm font-semibold text-text">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

/**
 * @component
 */
function BankingBankDetailsPage() {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        kicker={t('banking.bankDetailsPage.kicker')}
        title={t('nav.bankDetails')}
        description={t('banking.bankDetailsPage.description', {
          bankName: BANK_DETAILS.basicInfo[0]!.value,
        })}
      />
      <DetailSection title={t('banking.bankDetailsPage.basicInfo')} rows={BANK_DETAILS.basicInfo} />
      <DetailSection title={t('banking.bankDetailsPage.address')} rows={BANK_DETAILS.address} />
      <DetailSection
        title={t('banking.bankDetailsPage.organization')}
        rows={BANK_DETAILS.organization}
      />
    </div>
  )
}

export default BankingBankDetailsPage
